import { FC, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { Form, Select } from 'antd';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { FieldContainerIw as FieldContainerIwBase } from '@/_pages/interwill/components/FieldContainerIw';
import { Input } from '@/shared/components';
import { SelectCountry } from '@/shared/components/forms/fields/SelectCountry';
import { PassportDto } from '@/shared/lib/forms/personal-IDs/passport';
import { CollectionName } from '@/shared/lib/sj-orm/constants';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { DocumentType } from '@/shared/lib/sj-orm/models/document/document-base.dto';
import {
  DocumentCategory,
  DocumentCategoryToText,
  NationalIdDto,
  PersonalIdentifierType,
  SubstanceDto,
} from '@/shared/lib/sj-orm/models/document/personal-identifier.document.dto';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';
import { DateTimeIso } from '@/shared/types';
import { DatePicker } from '@/shared/uikit/DatePicker';
import { Text } from '@/shared/uikit/Text';
import { dates } from '@/shared/utils/dates';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { CitizenshipModalProps } from '@/widgets/modals/types';

import { ModalContainerV1 } from '../containers/ModalContainerV1';
import { InterwillStyledForm } from '../CreateEntityModal/styled';

import { CitizenshipParams } from './types';

export const CitezenshipModal: FC<CitizenshipModalProps> = ({
  additional,
  citizenshipIndex,
  beneficiaryData,
  updateBeneficiaryData,
}) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'citizenship' });
  const { t: td } = useTranslation('documents');

  const sjDatabase = useSJDatabase();
  const { keyPair } = useKeyPairStore();

  const onClose = useModalsStore((s) => s.closeModal);

  const citizenship = additional
    ? beneficiaryData.otherCitizenships?.[citizenshipIndex ?? 0]
    : beneficiaryData.primaryCitizenship;

  const {
    control,
    handleSubmit: formHandleSubmit,
    formState: { isSubmitting },
    watch,
    setValue,
  } = useForm<CitizenshipParams>({
    defaultValues: {
      id: citizenship?.id,
      country: citizenship?.country,
      documentType: citizenship?.documentType,
      documentNumber: citizenship?.number,
      issueDate: citizenship?.issueDate as DateTimeIso, // TODO WA-3907 dates
    },
  });

  const documentTypes = additional
    ? [
        PersonalIdentifierType.PASSPORT,
        PersonalIdentifierType.NATIONAL_ID,
        PersonalIdentifierType.RESIDENCE_PERMIT,
      ]
    : [PersonalIdentifierType.PASSPORT, PersonalIdentifierType.NATIONAL_ID];

  const selectedCountry = watch('country');
  const selectedDocumentType = watch('documentType');

  const previousCitizenships = [
    beneficiaryData.primaryCitizenship?.id,
    ...(beneficiaryData.otherCitizenships || []).map((n) => n.id),
  ].filter((n) => n !== citizenship?.id);

  const documentDataArr = sjDatabase.collections.personalIdentifiersDocuments?.findMany(
    (item) =>
      item.beneficiaryId === (beneficiaryData.id || 'me') &&
      item.personalIdentifierType === selectedDocumentType &&
      item.country === selectedCountry &&
      !previousCitizenships.includes(item.id),
  ) as SubstanceDto[];

  const documentData = documentDataArr?.[0] as unknown as PassportDto | NationalIdDto | undefined;

  const tab = useMemo(() => {
    if (!selectedCountry || !selectedDocumentType) return 0;
    if (documentData) {
      setValue('documentNumber', documentData.number ?? '');
      setValue(
        'issueDate',
        documentData.dateOfIssue
          ? dates.dayjsToIsoDateTime(dayjs(documentData.dateOfIssue)) // TODO WA-3907 dates
          : ('' as DateTimeIso), // TODO WA-3907 dates
      );
      return 1;
    }
    setValue('documentNumber', '');
    setValue('firstName', beneficiaryData?.firstName ?? '');
    setValue('lastName', beneficiaryData?.lastName ?? '');
    setValue('title', '');
    setValue('issueDate', '' as DateTimeIso); // TODO WA-3907 dates
    return 2;
  }, [selectedCountry, selectedDocumentType, documentData]);

  const selectedDocumentText = useMemo(() => {
    if (selectedDocumentType in DocumentCategoryToText) {
      return td(DocumentCategoryToText[selectedDocumentType as DocumentCategory]);
    }
    return selectedDocumentType;
  }, [selectedDocumentType]);

  const onSubmit = async (values: CitizenshipParams) => {
    const dateIso = dayjs(values.issueDate).toISOString();
    let id = null;
    if (documentData) {
      await sjDatabase.collections.personalIdentifiersDocuments?.update({
        ...documentDataArr![0],
        dateOfIssue: dateIso,
        number: values.documentNumber,
      } as SubstanceDto);
      id = documentDataArr![0].id;
    } else {
      id = sjDatabase.generateId(CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS);
      await sjDatabase.collections.personalIdentifiersDocuments?.create(
        {
          id: id,
          type: DocumentType.PERSONAL_IDENTIFIERS,
          personalIdentifierType: values.documentType as PersonalIdentifierType,
          dateOfIssue: dateIso,
          number: values.documentNumber,
          citizenship: values.country,
          country: values.country,
          name: values.title,
          beneficiaryId: beneficiaryData.id || 'me',
          names: values.firstName,
          surnames: values.lastName,
        } as SubstanceDto,
        [keyPair?.publicKey || ''],
      );
    }
    if (additional) {
      const citizenships = [...(beneficiaryData.otherCitizenships || [])];
      citizenships[citizenshipIndex ?? 0] = {
        id,
        country: selectedCountry,
        documentType: selectedDocumentType,
        issueDate: dateIso,
        number: values.documentNumber,
      };
      updateBeneficiaryData({ otherCitizenships: citizenships });
    } else {
      updateBeneficiaryData({
        primaryCitizenship: {
          id,
          country: selectedCountry,
          documentType: selectedDocumentType,
          issueDate: dateIso,
          number: values.documentNumber,
        },
      });
    }

    onClose();
  };

  return (
    <ModalContainerV1 title={t('title')}>
      <InterwillStyledForm onFinish={formHandleSubmit(onSubmit)}>
        <InnerForm>
          <Controller
            name={'country'}
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <SelectCountry placeholder={t(`fields.${field.name}.placeholder`)} {...field} />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />
          <Controller
            name={'documentType'}
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Select
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      {...field}
                      options={documentTypes.map((item) => ({
                        label: td(DocumentCategoryToText[item]),
                        value: item,
                      }))}
                    />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />
          {tab === 2 && (
            <>
              <Text>
                {selectedDocumentText} {t('documentIsMissing')}
              </Text>
              <Controller
                name={'title'}
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => {
                  return (
                    <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                      <Form.Item
                        validateStatus={fieldState.error ? 'error' : ''}
                        help={fieldState.error?.message}
                      >
                        <Input
                          placeholder={t(`fields.${field.name}.placeholder`)}
                          type={'text'}
                          {...field}
                        />
                      </Form.Item>
                    </FieldContainerIw>
                  );
                }}
              />
              <Row>
                <Controller
                  name={'firstName'}
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => {
                    return (
                      <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                        <Form.Item
                          validateStatus={fieldState.error ? 'error' : ''}
                          help={fieldState.error?.message}
                        >
                          <Input
                            placeholder={t(`fields.${field.name}.placeholder`)}
                            type={'text'}
                            {...field}
                          />
                        </Form.Item>
                      </FieldContainerIw>
                    );
                  }}
                />
                <Controller
                  name={'lastName'}
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => {
                    return (
                      <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                        <Form.Item
                          validateStatus={fieldState.error ? 'error' : ''}
                          help={fieldState.error?.message}
                        >
                          <Input
                            placeholder={t(`fields.${field.name}.placeholder`)}
                            type={'text'}
                            {...field}
                          />
                        </Form.Item>
                      </FieldContainerIw>
                    );
                  }}
                />
              </Row>
            </>
          )}
          {tab > 0 && (
            <>
              <Controller
                name={'documentNumber'}
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => {
                  return (
                    <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                      <Form.Item
                        validateStatus={fieldState.error ? 'error' : ''}
                        help={fieldState.error?.message}
                      >
                        <Input
                          placeholder={t(`fields.${field.name}.placeholder`)}
                          type={'text'}
                          {...field}
                        />
                      </Form.Item>
                    </FieldContainerIw>
                  );
                }}
              />
              <Controller
                name={'issueDate'}
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => {
                  return (
                    <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                      <Form.Item
                        validateStatus={fieldState.error ? 'error' : ''}
                        help={fieldState.error?.message}
                      >
                        <DatePicker
                          placeholder={t(`fields.${field.name}.placeholder`)}
                          {...field}
                        />
                      </Form.Item>
                    </FieldContainerIw>
                  );
                }}
              />
            </>
          )}
        </InnerForm>
        <Buttons>
          <ButtonIw loading={isSubmitting} disabled={isSubmitting} htmlType="submit">
            {t('btn')}
          </ButtonIw>
        </Buttons>
      </InterwillStyledForm>
    </ModalContainerV1>
  );
};

const InnerForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 420px;
  overflow: auto;
  padding: 0 2px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 40px;
`;

const FieldContainerIw = styled(FieldContainerIwBase)`
  flex: 0 50%;
`;
